import { useState } from "react";
import { FaEye, FaEyeSlash, FaInfoCircle } from "react-icons/fa";
import InputMask from 'react-input-mask';
import HintTrigger from "../HintTrigger/HintTrigger";

type FloatingInputProps = {
  label: string;
  name?: string;
  type?: React.HTMLInputTypeAttribute;
  value?: string | number;
  checked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  disabled?: boolean;
  required?: boolean;
  mask?: string;
  min?: number;
  max?: number;
  step?: number;
  tooltip?: string;
  autoComplete?: string;
};

const FloatingInput = ({ 
    label,
    name,
    type = "text",
    value,
    checked,
    onChange,
    readOnly = false,
    disabled = false,
    required = false,
    mask,
    min,
    max,
    step,
    tooltip,
    autoComplete
  }: FloatingInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isActive = isFocused || (value && value.toString().length > 0);
  const isPassword = type === "password";
  const isDate = type === "date";
  const hasTooltip = Boolean(tooltip);
  const shouldHideDateText = isDate && !isActive && !readOnly;
  const paddingRight = isPassword
    ? hasTooltip
      ? "pr-14"
      : "pr-10"
    : "pr-4";

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  if (type === "checkbox") {
    return (
      <div className="flex items-center gap-2 mb-3 md:mb-4">
        <input
          type="checkbox"
          name={name}
          checked={checked}
          onChange={onChange}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
        />
        {label && (
          <label htmlFor={name} className="text-gray-500 text-sm">
            {label} {required && <span className="text-primary-600">*</span>}
          </label>
        )}
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {mask ? (
        <InputMask
          mask={mask || ''}
          maskChar={null}
          name={name}
          value={value}
          onChange={onChange}
          onFocus={() => !readOnly && setIsFocused(true)}
          onBlur={() => !readOnly && setIsFocused(false)}
          readOnly={readOnly}
          disabled={disabled}
        >
          {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
            <input
              {...inputProps}
              value={value}
              onChange={onChange}
              type={isPassword ? (showPassword ? "text" : "password") : type}
              required={required}
              readOnly={readOnly}
              tabIndex={readOnly ? -1 : undefined}
              autoComplete={autoComplete}
              className={`
                peer w-full border border-gray-300 rounded px-3 pt-5 pb-2 text-xs sm:text-sm focus:outline-none focus:ring-0 
                ${readOnly 
                  ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                  : "focus:ring-primary-600 focus:border-primary-600"
                } ${shouldHideDateText ? "text-transparent focus:text-gray-900" : ""} ${paddingRight}`
              }
            />
          )}
        </InputMask>
      ) : (
        <input
          name={name}
          value={value}
          onChange={onChange}
          onFocus={() => !readOnly && setIsFocused(true)}
          onBlur={() => !readOnly && setIsFocused(false)}
          type={isPassword ? (showPassword ? "text" : "password") : type}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          autoComplete={autoComplete}
          className={`
            peer w-full border border-gray-300 rounded px-3 pt-5 pb-2 text-sm focus:outline-none focus:ring-0
            ${readOnly 
              ? "bg-gray-100 text-gray-500 cursor-not-allowed"
              : "focus:ring-primary-600 focus:border-primary-600"
            } ${shouldHideDateText ? "text-transparent focus:text-gray-900" : ""} ${paddingRight}`
          }
        />
      )}   

      <label
        className={`absolute left-3 transition-all duration-200 pointer-events-none ${isActive ? 'top-1 text-xs text-primary-600' : 'top-3 text-xs sm:text-sm text-gray-500'}`}
      >
        {label}{required && <span>*</span>}
      </label>
      {tooltip && (
        <HintTrigger
          label="Requisitos de senha"
          hint={tooltip}
          className={`absolute inline-flex text-gray-400 cursor-help top-1/2 -translate-y-1/2 ${
            isPassword ? "right-9" : "right-3"
          }`}
        >
          <FaInfoCircle />
        </HintTrigger>
      )}

      {isPassword && (
        <button
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500 hover:text-gray-700"
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={(event) => {
            event.stopPropagation();
            togglePasswordVisibility();
          }}
          aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
        >
          {showPassword ? <FaEyeSlash /> : <FaEye />}
        </button>
      )}
    </div>
  );
};

export default FloatingInput;
